import { useMutation, useQueryClient } from '@tanstack/react-query'
import { editPresc } from '../../../../apis/editPresc'
import { resetPrescriptionsCache } from '../../../../shared'
import {
  BasicResult,
  Medication,
  Prescription,
} from '../../../../types/models/Presc'

type EditBasicResultParams = {
  prescription: Prescription
  key: keyof BasicResult
  value: string
}

type EditRPnumberItemParams = {
  prescription: Prescription
  rpIndex: number
  key: 'RP番号' | '用法名称' | '調剤数量'
  value: string
}

type EditMedResultParams = {
  prescription: Prescription
  rpIndex: number
  medIndex: number
  // bboxはテキスト編集対象ではない(document_ai/bbox-corrections APIで別途修正)
  key: Exclude<keyof Medication, 'bbox'>
  value: string
}

/**
 * 処方箋の基本情報を編集する Mutation
 */
export const useEditBasicResultMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prescription, key, value }: EditBasicResultParams) => {
      const newPrescription = { ...prescription }
      // JAHIS QR直読は basic_result 自体が無く、編集対象の項目UIも描画されないため到達しない。
      if (!newPrescription.basic_result)
        throw new Error(
          '読み取り結果の無い処方箋(JAHIS QR直読)は編集できません'
        )
      newPrescription.basic_result[key]['selected_option'] = value
      return editPresc(key, newPrescription)
    },
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}

/**
 * 処方箋の RP 番号項目を編集する Mutation
 */
export const useEditRPnumberItemMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      prescription,
      rpIndex,
      key,
      value,
    }: EditRPnumberItemParams) => {
      const newPrescription = { ...prescription }
      // JAHIS QR直読は med_result 自体が無く、編集対象の項目UIも描画されないため到達しない。
      if (!newPrescription.med_result)
        throw new Error(
          '読み取り結果の無い処方箋(JAHIS QR直読)は編集できません'
        )
      newPrescription.med_result[rpIndex][key]['selected_option'] = value
      return editPresc(key, newPrescription)
    },
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}

/**
 * 処方箋の薬剤情報を編集する Mutation
 */
export const useEditMedResultMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      prescription,
      rpIndex,
      medIndex,
      key,
      value,
    }: EditMedResultParams) => {
      const newPrescription = { ...prescription }
      // JAHIS QR直読は med_result 自体が無く、編集対象の項目UIも描画されないため到達しない。
      if (!newPrescription.med_result)
        throw new Error(
          '読み取り結果の無い処方箋(JAHIS QR直読)は編集できません'
        )
      newPrescription.med_result[rpIndex]['med_result'][medIndex][key][
        'selected_option'
      ] = value
      return editPresc(key, newPrescription)
    },
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}
